import { useCallback } from 'react';
import { renderToString } from 'react-dom/server';
import { ServerStyleSheet } from 'styled-components';
import {
    UseMutateFunction,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';

import { StaticNecessityAppWrapper } from 'src/App';

import {
    PostDocumentsPayload,
    SectionId,
    SectionInput,
} from '../documents.types';

import * as S from '../documentRow.style';

import {
    getDocumentSections,
    postDocuments,
    useGetDocumentSectionsKey,
} from './documents.api.hooks';

export const useGetSections = () => {
    const { isLoading, isFetching, isRefetching, data } = useQuery({
        queryKey: [useGetDocumentSectionsKey],
        queryFn: async () => await getDocumentSections(),
    });

    return {
        isLoading: isLoading || isFetching || isRefetching,
        data: data ?? [],
    };
};

interface UsePostDocuments {
    mutate: UseMutateFunction<void, unknown, PostDocumentsPayload, unknown>;
    isLoading: boolean;
    isError: boolean;
}

const usePostDocuments = (): UsePostDocuments => {
    const { mutate, isPending, isError } = useMutation({ mutationFn: postDocuments });

    return {
        mutate,
        isLoading: isPending,
        isError,
    };
};

export const useDocuments = () => {
    const queryClient = useQueryClient();
    const { data, isLoading } = useGetSections();
    const { isLoading: isPostingDocuments, mutate } = usePostDocuments();

    const onValidateSection = useCallback(
        (
            sectionId: SectionId,
            inputs: (SectionInput | null)[],
            generatedHtml: string | React.JSX.Element | React.JSX.Element[],
        ) => {
            const foundSection = data.find(
                (section) => section.id.sectionId === sectionId.sectionId,
            );

            const allSectionValidated = inputs.every((input) => !!input);

            if (!foundSection || !allSectionValidated) {
                return;
            }

            const inputWithNoNullValues = inputs as SectionInput[];

            const reactComponent = (
                <StaticNecessityAppWrapper>
                    <S.DocumentContainer>{generatedHtml}</S.DocumentContainer>
                </StaticNecessityAppWrapper>
            );
            const sheet = new ServerStyleSheet();
            const html = renderToString(sheet.collectStyles(reactComponent));
            const styleTags = sheet.getStyleTags();
            const htmlWithStyle = `<html><head>${styleTags}</head><body>${html}</body></html>`;

            mutate(
                {
                    section: foundSection,
                    renderedHtml: htmlWithStyle,
                    inputs: inputWithNoNullValues,
                },
                {
                    onSuccess: () => {
                        queryClient.invalidateQueries({
                            queryKey: [useGetDocumentSectionsKey],
                        });
                    },
                    onError: () => {
                        queryClient.invalidateQueries({
                            queryKey: [useGetDocumentSectionsKey],
                        });
                    },
                },
            );
        },
        [data],
    );

    return {
        data,
        isLoading,
        isPostingDocuments,
        onValidateSection,
    };
};
